import type { SurveyQuestion, SurveySection } from '@/api/surveys';

import { DEFAULT_RATING_CONFIG } from './constants';
import {
    cloneSnapshot,
    createOption,
    createQuestion,
    createSection,
    createUniqueFieldName,
    defaultOptions,
    defaultSnapshot,
    findQuestionPosition,
    getAllQuestions,
    isOptionFieldType,
    normalizeOption,
    normalizeRatingConfig,
    normalizeSnapshotForBuilder,
    resolveFieldType,
    supportsPlaceholder,
    validationForType,
} from './builderUtils';
import type { BuilderState, FieldType, QuestionPatch, RatingPatch } from './types';

export type BuilderAction =
    | {
          type: 'hydrate';
          snapshot: BuilderState['snapshot'];
          activeQuestionId?: string | null;
      }
    | { type: 'setActiveQuestion'; questionId: string | null }
    | { type: 'updateSurveyTitle'; value: string }
    | { type: 'updateSurveyInstructions'; value: string }
    | { type: 'addSection'; afterSectionId?: string; section?: SurveySection }
    | { type: 'updateSection'; sectionId: string; patch: { title?: string; description?: string } }
    | { type: 'deleteSection'; sectionId: string }
    | { type: 'moveSection'; sectionId: string; direction: 'up' | 'down' }
    | { type: 'addQuestion'; sectionId: string }
    | { type: 'duplicateQuestion'; questionId: string }
    | { type: 'deleteQuestion'; questionId: string }
    | { type: 'moveQuestion'; questionId: string; direction: 'up' | 'down' }
    | { type: 'updateQuestion'; questionId: string; patch: QuestionPatch }
    | { type: 'updateQuestionLabel'; questionId: string; value: string }
    | { type: 'updateQuestionType'; questionId: string; fieldType: FieldType }
    | { type: 'addOption'; questionId: string }
    | { type: 'updateOption'; questionId: string; optionIndex: number; value: string }
    | { type: 'deleteOption'; questionId: string; optionIndex: number }
    | { type: 'updateRating'; questionId: string; patch: RatingPatch };

export const initialBuilderState: BuilderState = {
    snapshot: defaultSnapshot(),
    activeQuestionId: null,
};

const updateByQuestionId = (
    state: BuilderState,
    questionId: string,
    updater: (question: SurveyQuestion, snapshot: BuilderState['snapshot']) => void,
) => {
    const snapshot = cloneSnapshot(state.snapshot);
    const position = findQuestionPosition(snapshot, questionId);

    if (!position) return state;

    const question = snapshot.sections[position.sectionIndex].questions[position.questionIndex];
    updater(question, snapshot);

    return { ...state, snapshot };
};

export const builderReducer = (state: BuilderState, action: BuilderAction): BuilderState => {
    switch (action.type) {
        case 'hydrate': {
            const snapshot = normalizeSnapshotForBuilder(action.snapshot);
            const activeQuestionId = action.activeQuestionId ?? state.activeQuestionId;
            const activeQuestionExists = Boolean(
                activeQuestionId && findQuestionPosition(snapshot, activeQuestionId),
            );

            return {
                snapshot,
                activeQuestionId: activeQuestionExists ? activeQuestionId : null,
            };
        }

        case 'setActiveQuestion':
            return { ...state, activeQuestionId: action.questionId };

        case 'updateSurveyTitle':
            return {
                ...state,
                snapshot: {
                    ...state.snapshot,
                    name: action.value,
                },
            };

        case 'updateSurveyInstructions':
            return {
                ...state,
                snapshot: {
                    ...state.snapshot,
                    instructions: action.value,
                },
            };

        case 'addSection': {
            const snapshot = cloneSnapshot(state.snapshot);
            const section = action.section ?? createSection();
            const afterIndex = action.afterSectionId
                ? snapshot.sections.findIndex((item) => item.id === action.afterSectionId)
                : -1;

            if (afterIndex >= 0) {
                snapshot.sections.splice(afterIndex + 1, 0, section);
            } else {
                snapshot.sections.push(section);
            }

            return { ...state, snapshot, activeQuestionId: null };
        }

        case 'updateSection': {
            const snapshot = cloneSnapshot(state.snapshot);
            const section = snapshot.sections.find((item) => item.id === action.sectionId);

            if (!section) return state;

            section.title = action.patch.title ?? section.title;
            section.description = action.patch.description ?? section.description;

            return { ...state, snapshot };
        }

        case 'deleteSection': {
            if (state.snapshot.sections.length <= 1) return state;

            const snapshot = cloneSnapshot(state.snapshot);
            const sectionIndex = snapshot.sections.findIndex(
                (section) => section.id === action.sectionId,
            );
            if (sectionIndex < 0) return state;

            const [removedSection] = snapshot.sections.splice(sectionIndex, 1);
            const removedActiveQuestion = removedSection.questions.some(
                (question) => question.id === state.activeQuestionId,
            );

            return {
                ...state,
                snapshot,
                activeQuestionId: removedActiveQuestion ? null : state.activeQuestionId,
            };
        }

        case 'moveSection': {
            const snapshot = cloneSnapshot(state.snapshot);
            const index = snapshot.sections.findIndex((section) => section.id === action.sectionId);
            const targetIndex = action.direction === 'up' ? index - 1 : index + 1;

            if (index < 0 || targetIndex < 0 || targetIndex >= snapshot.sections.length) {
                return state;
            }

            const [section] = snapshot.sections.splice(index, 1);
            snapshot.sections.splice(targetIndex, 0, section);

            return { ...state, snapshot };
        }

        case 'addQuestion': {
            const snapshot = cloneSnapshot(state.snapshot);
            const section = snapshot.sections.find((item) => item.id === action.sectionId);
            if (!section) return state;

            const question = createQuestion(getAllQuestions(snapshot));
            section.questions.push(question);

            return {
                ...state,
                snapshot,
                activeQuestionId: question.id ?? null,
            };
        }

        case 'duplicateQuestion': {
            const snapshot = cloneSnapshot(state.snapshot);
            const position = findQuestionPosition(snapshot, action.questionId);
            if (!position) return state;

            const section = snapshot.sections[position.sectionIndex];
            const source = section.questions[position.questionIndex];
            const duplicate: SurveyQuestion = {
                ...source,
                id: createQuestion().id,
                field_name: createUniqueFieldName(
                    source.field_label?.trim() || source.field_name || 'pergunta',
                    getAllQuestions(snapshot),
                ),
                config: source.config ? { ...source.config } : undefined,
                options: source.options?.map(normalizeOption),
            };

            section.questions.splice(position.questionIndex + 1, 0, duplicate);

            return {
                ...state,
                snapshot,
                activeQuestionId: duplicate.id ?? null,
            };
        }

        case 'deleteQuestion': {
            const snapshot = cloneSnapshot(state.snapshot);
            const position = findQuestionPosition(snapshot, action.questionId);
            if (!position) return state;

            snapshot.sections[position.sectionIndex].questions.splice(position.questionIndex, 1);

            return {
                ...state,
                snapshot,
                activeQuestionId:
                    state.activeQuestionId === action.questionId ? null : state.activeQuestionId,
            };
        }

        case 'moveQuestion': {
            const snapshot = cloneSnapshot(state.snapshot);
            const position = findQuestionPosition(snapshot, action.questionId);
            if (!position) return state;

            const questions = snapshot.sections[position.sectionIndex].questions;
            const targetIndex =
                action.direction === 'up' ? position.questionIndex - 1 : position.questionIndex + 1;

            if (targetIndex < 0 || targetIndex >= questions.length) return state;

            const [question] = questions.splice(position.questionIndex, 1);
            questions.splice(targetIndex, 0, question);

            return { ...state, snapshot };
        }

        case 'updateQuestion':
            return updateByQuestionId(state, action.questionId, (question) => {
                Object.assign(question, action.patch);
                question.config = {
                    ...(question.config ?? {}),
                    ...(action.patch.config ?? {}),
                };
            });

        case 'updateQuestionLabel':
            return updateByQuestionId(state, action.questionId, (question, snapshot) => {
                question.field_label = action.value;
                question.field_name = createUniqueFieldName(
                    action.value || 'pergunta',
                    getAllQuestions(snapshot),
                    question.id,
                );
            });

        case 'updateQuestionType':
            return updateByQuestionId(state, action.questionId, (question) => {
                const existingConfig = question.config ?? {};
                const nextConfig: NonNullable<SurveyQuestion['config']> = {
                    help_text: existingConfig.help_text ?? '',
                };

                if (supportsPlaceholder(action.fieldType)) {
                    nextConfig.placeholder = existingConfig.placeholder ?? '';
                }

                question.field_type = action.fieldType;
                question.validation = validationForType(action.fieldType);
                question.config = nextConfig;

                if (isOptionFieldType(action.fieldType)) {
                    question.options = question.options?.length
                        ? question.options.map(normalizeOption)
                        : defaultOptions();

                    if (action.fieldType === 'select') {
                        question.config.allow_other = Boolean(existingConfig.allow_other);
                    }
                } else {
                    delete question.options;
                }

                if (action.fieldType === 'rating') {
                    question.config.rating = normalizeRatingConfig(existingConfig.rating);
                }
            });

        case 'addOption':
            return updateByQuestionId(state, action.questionId, (question) => {
                const options = (question.options ?? []).map(normalizeOption);
                question.options = [...options, createOption(options.length + 1)];
            });

        case 'updateOption':
            return updateByQuestionId(state, action.questionId, (question) => {
                const options = (question.options ?? []).map(normalizeOption);
                if (!options[action.optionIndex]) return;

                options[action.optionIndex] = {
                    label: action.value,
                    value: createUniqueFieldName(action.value || 'opcao', []),
                };
                question.options = options;
            });

        case 'deleteOption':
            return updateByQuestionId(state, action.questionId, (question) => {
                question.options = (question.options ?? [])
                    .map(normalizeOption)
                    .filter((_, index) => index !== action.optionIndex);
            });

        case 'updateRating':
            return updateByQuestionId(state, action.questionId, (question) => {
                const fieldType = resolveFieldType(question);
                if (fieldType !== 'rating') return;

                question.config = {
                    ...(question.config ?? {}),
                    rating: normalizeRatingConfig({
                        ...(question.config?.rating ?? DEFAULT_RATING_CONFIG),
                        ...action.patch,
                    }),
                };
            });

        default:
            return state;
    }
};
